import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { UseraccessserviceService } from '../useraccessservice.service';
import { ModulemanagementService } from '../modules/modulemanagement.service';
import { OfflineDbService } from './offline-db.service';
import { OfflineBootstrapCacheService } from './offline-bootstrap-cache.service';
import { EntitlementRecord } from './offline-db.types';
import { OfflineMediaService } from './offline-media.service';
import { buildContentKey, decodeJwtPayload, getCurrentSchoolSubdomain, getCurrentUserId } from './offline-cache-key';

interface EntitlementResponse {
  token: string;
}

export interface EnrolledModuleSummary {
  id: number;
  courseTitle: string;
  imageUrl?: string;
}

const ENROLLED_MODULES_CACHE_KIND = 'enrolledmodules';
// getmyenrolledmodules is paginated with no unpaginated variant — this is a pragmatic cap, not
// a real "all." A student enrolled in >500 courses at one school isn't a real case.
const ENROLLED_MODULES_FETCH_LIMIT = 500;

// Wires the `entitlement` store P1 schema'd but never wrote. Called on login and on the
// ->online connectivity transition (see offline-connectivity.service.ts consumers). Never
// throws — a failed refresh just leaves the existing (possibly now-expired) entitlement in
// place, which the downloads hub's own entitlement check (offline-content-reader.service.ts /
// managedownloads.component.ts) already handles correctly by refusing to serve.
@Injectable({
  providedIn: 'root',
})
export class OfflineEntitlementService {
  constructor(
    private http: HttpClient,
    private offlineDb: OfflineDbService,
    private useraccessservice: UseraccessserviceService,
    private modulemanager: ModulemanagementService,
    private bootstrapCache: OfflineBootstrapCacheService,
    private offlineMedia: OfflineMediaService,
  ) {}

  // Returns whether this call actually got a fresh entitlement from the server. Callers MUST
  // check it before sweeping — see sweepIfExpired. It still never throws: a failed refresh is
  // normal (offline, flaky connection) and must not break startup.
  async refresh(): Promise<boolean> {
    try {
      const token = this.useraccessservice.getCurrentUserToken();
      const school = getCurrentSchoolSubdomain();
      if (!token || !school) return false;

      const resp = await this.http
        .post<EntitlementResponse>(environment.ImpataAppAPIRoot + 'offline/entitlement', {})
        .toPromise();
      if (!resp?.token) return false;

      // Local, unverified decode — same caveat as decodeJwtPayload's own doc comment: this
      // only tells the client what the entitlement *claims*, it never substitutes for the
      // server actually verifying the signature. It gates local decryption of content this
      // user already downloaded; the API remains the real authorization boundary.
      const claims = decodeJwtPayload(resp.token);
      const user = claims?.['ent_user'];
      const moduleIdsRaw = claims?.['ent_modules'];
      const expRaw = claims?.['exp'];
      if (typeof user !== 'number' && typeof user !== 'string') return false;

      const moduleIds =
        typeof moduleIdsRaw === 'string' && moduleIdsRaw.length
          ? moduleIdsRaw
              .split(',')
              .map((s) => Number(s))
              .filter((n) => Number.isFinite(n))
          : [];
      // JWT "exp" is seconds since epoch, not ms.
      const expiresAt = typeof expRaw === 'number' ? expRaw * 1000 : Date.now();

      const userId = Number(user);
      const record: EntitlementRecord = {
        id: `${school}:${userId}`,
        school,
        user: userId,
        signedBlob: resp.token,
        issuedAt: Date.now(),
        expiresAt,
        moduleIds,
      };
      await this.offlineDb.putEntitlement(record);
      return true;
    } catch (err) {
      console.error('[offline] entitlement refresh failed', err);
      return false;
    }
  }

  // Powers the downloads hub's "show every enrolled course, not just ones with something
  // saved" requirement — a normal API call, cached the same way offline-bootstrap-cache.service.ts
  // already caches schoolconfig/offline-session, not through any route-matching machinery.
  async refreshEnrolledModuleSummaries(): Promise<void> {
    // Signed out (e.g. on the login page): the endpoint answers 401, which the interceptor treats
    // as an expired session and reloads the page — and a reconnect would do it all over again.
    if (!this.useraccessservice.getCurrentUserToken()) return;
    try {
      const url = this.modulemanager.getMyEnrolledModulesURL();
      const modules = await this.modulemanager.getModules(url, 0, ENROLLED_MODULES_FETCH_LIMIT).toPromise();
      await this.bootstrapCache.cache(ENROLLED_MODULES_CACHE_KIND, modules);
    } catch (err) {
      console.error('[offline] enrolled-modules cache refresh failed', err);
    }
  }

  async getValid(): Promise<EntitlementRecord | null> {
    const school = getCurrentSchoolSubdomain();
    const userId = getCurrentUserId(this.useraccessservice.getCurrentUserToken());
    if (!school || !userId) return null;

    const record = await this.offlineDb.getEntitlement(`${school}:${userId}`);
    if (!record || record.expiresAt < Date.now()) return null;
    return record;
  }

  // Removes content for modules a CURRENT entitlement no longer covers — unenrollment,
  // refund, expulsion.
  //
  // DO NOT make this delete when there is no valid entitlement, which is what it used to do.
  // getValid() returns null both when the entitlement is absent and when it has merely
  // expired, and the loop below treated null as "nothing is covered" and deleted every saved
  // lesson and note on the device. refresh() swallows its own errors and always resolves, so
  // app.component's `refresh().then(() => sweepIfExpired())` ran this on every single startup
  // regardless of whether the refresh had worked. A student who stayed offline past the
  // entitlement TTL, or who simply opened the app on a bad connection, lost everything they
  // had downloaded — and offline there was no way to get it back.
  //
  // Absent or expired is not evidence that anything was revoked; it is an absence of
  // evidence either way. Keeping unreadable ciphertext costs nothing and leaks nothing:
  // offline-content-reader.service.ts independently refuses to serve content without a valid
  // entitlement, so the fail-closed guarantee never depended on this deletion.
  async sweepIfExpired(): Promise<void> {
    const school = getCurrentSchoolSubdomain();
    const userId = getCurrentUserId(this.useraccessservice.getCurrentUserToken());
    if (!school || !userId) return;

    const valid = await this.getValid();
    if (!valid) return; // no current entitlement to judge coverage against — see above

    const entries = await this.offlineDb.listIndexEntries(school, userId);
    for (const entry of entries) {
      const stillCovered = valid.moduleIds.includes(entry.moduleId);
      if (stillCovered) continue;
      await this.offlineDb.deleteContent(buildContentKey(school, userId, entry.kind, entry.resourceId, entry.currentVersion));
      await this.offlineDb.deleteIndexEntry(entry.key);
    }

    // Saved videos are not index entries (they live in `media` plus a file), so the loop above
    // never saw them. Same rule, same guard: only against a current entitlement.
    await this.offlineMedia
      .deleteVideosNotCoveredBy(school, userId, valid.moduleIds)
      .catch((err) => console.error('[offline] could not remove videos for courses no longer covered', err));
  }
}
